import React, { createContext, useContext, useRef, useCallback } from 'react';

// Create the AutoFocusContext
const AutoFocusContext = createContext();

// Provider component
export const AutoFocusProvider = ({ children }) => {
	const registeredInputsRef = useRef(new Map()); // Map of componentId -> inputRef

	// Register an input for auto-focus
	const registerInput = useCallback((componentId, inputRef) => {
		registeredInputsRef.current.set(componentId, inputRef);
	}, []);

	// Unregister an input
	const unregisterInput = useCallback((componentId) => {
		registeredInputsRef.current.delete(componentId);
	}, []);

	// Focus the nearest input (or all registered inputs)
	const focusNearestInput = useCallback((excludeComponentId = null) => {
		const inputs = Array.from(registeredInputsRef.current.entries());
		
		// If there's only one input, focus it
		if (inputs.length === 1) {
			const [, inputRef] = inputs[0];
			if (inputRef && inputRef.current && typeof inputRef.current.focus === 'function') {
				inputRef.current.focus();
			}
			return;
		}

		// If there are multiple inputs, focus the first one that's not excluded
		for (const [componentId, inputRef] of inputs) {
			if (componentId !== excludeComponentId && inputRef && inputRef.current && typeof inputRef.current.focus === 'function') {
				inputRef.current.focus();
				break;
			}
		}
	}, []);

	// Focus a specific input by component ID
	const focusInputById = useCallback((componentId) => {
		const inputRef = registeredInputsRef.current.get(componentId);
		if (inputRef && inputRef.current && typeof inputRef.current.focus === 'function') {
			inputRef.current.focus();
		}
	}, []);

	// Get all registered input IDs
	const getRegisteredInputs = useCallback(() => {
		return Array.from(registeredInputsRef.current.keys());
	}, []);

	const value = {
		registerInput,
		unregisterInput,
		focusNearestInput,
		focusInputById,
		getRegisteredInputs,
	};

	return (
		<AutoFocusContext.Provider value={value}>
			{children}
		</AutoFocusContext.Provider>
	);
};

// Hook to use the auto-focus context
export const useAutoFocusContext = () => {
	const context = useContext(AutoFocusContext);
	if (!context) {
		// Return null instead of throwing error to make it optional
		return null;
	}
	return context;
};

export default AutoFocusContext;
